import React from 'react';
import { useAppSelector } from 'hooks';

import { AuthTags } from '~constants/authTags';

import { Button } from '~components';

import { PaymentBatchClient } from '~direct-debits/models';

import { selectUserTags } from '~main/selectors/login';

interface ComponentProps {
  row: PaymentBatchClient;
  onViewInvoices?: (clientId: number) => void;
  onRefund?: (row: PaymentBatchClient) => void;
  onReverse?: (row: PaymentBatchClient) => void;
}

// A collected payment can be refunded; an in-flight one can be reversed
// (cancelled). The two actions are mutually exclusive — the line's status
// picks which (if either) appears, so the operator never has to choose.
const REFUNDABLE_TAG = 'paid_out';
const REVERSIBLE_TAGS = new Set(['submitted', 'pending_customer_approval']);

const BatchClientRowAction: React.FC<ComponentProps> = ({ row, onViewInvoices, onRefund, onReverse }) => {
  const userTags = useAppSelector(selectUserTags);
  const canRefund = userTags.includes(AuthTags.DIRECTDEBITS.REFUND);
  const canReverse = userTags.includes(AuthTags.DIRECTDEBITS.REVERSE);

  const statusTag = row.payment_status?.tag ?? null;
  // The controls need the payment line id to act on; the batch-detail API
  // supplies it, and they stay hidden until it's present.
  const hasLine = Boolean(row.payment_batch_item_id);

  const showRefund = hasLine && canRefund && !row.refund && statusTag === REFUNDABLE_TAG && Boolean(onRefund);
  const showReverse =
    hasLine && canReverse && statusTag !== null && REVERSIBLE_TAGS.has(statusTag) && Boolean(onReverse);

  const viewDisabled = !onViewInvoices || row.invoice_count === 0;

  return (
    <div className="d-flex gap-2 justify-content-end">
      {showRefund && (
        <Button
          id={`dd-payment-batch-refund-${row.client_id}`}
          data-cy={`dd-payment-batch-refund-${row.client_id}`}
          size="sm"
          color="danger"
          outline
          onClick={() => onRefund?.(row)}
        >
          Refund
        </Button>
      )}
      {showReverse && (
        <Button
          id={`dd-payment-batch-reverse-${row.client_id}`}
          data-cy={`dd-payment-batch-reverse-${row.client_id}`}
          size="sm"
          color="primary"
          outline
          onClick={() => onReverse?.(row)}
        >
          Reverse
        </Button>
      )}
      <Button
        id={`dd-payment-batch-client-view-${row.client_id}`}
        data-cy={`dd-payment-batch-client-view-${row.client_id}`}
        size="sm"
        color="primary"
        outline
        disabled={viewDisabled}
        onClick={() => onViewInvoices?.(row.client_id)}
      >
        View Invoices
      </Button>
    </div>
  );
};

export default BatchClientRowAction;
